<template>
  <ehc-page v-if="userHasPermission(['superAdmin'])">
    <ehc-header text="Advanced Analytics"></ehc-header>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mt-n2 mb-3"
    ></v-progress-linear>

    <v-alert v-if="error" type="error" outlined class="mb-4">
      {{ error }}
    </v-alert>

    <ehc-sheet>
      <v-card flat class="mb-4 pa-4" outlined>
        <v-row align="center">
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="dateRange"
              :items="dateRangeOptions"
              label="Date range"
              outlined
              dense
              hide-details
              @change="loadAnalytics"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-btn color="primary" outlined :loading="loading" @click="loadAnalytics">
              <v-icon left small>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </v-col>
          <v-col cols="12" md="6" class="text-caption text-grey text-md-right">
            <span v-if="data?.range">
              {{ formatRangeLabel(data.range) }}
            </span>
          </v-col>
        </v-row>
      </v-card>

      <v-row v-if="data" class="mb-4">
        <v-col
          v-for="card in summaryCards"
          :key="card.label"
          cols="12"
          sm="6"
          md="4"
          lg="2"
        >
          <v-card outlined class="pa-3 text-center fill-height">
            <div class="text-overline text-grey">{{ card.label }}</div>
            <div class="text-h6 font-weight-bold">{{ card.value }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-tabs v-if="data" v-model="activeTab" class="mb-4">
        <v-tab>Property lifecycle</v-tab>
        <v-tab>Guest visits</v-tab>
        <v-tab>Platform activity</v-tab>
      </v-tabs>

      <v-tabs-items v-if="data" v-model="activeTab">
        <v-tab-item>
          <v-row>
            <v-col cols="12" lg="6">
              <v-card outlined class="pa-4 mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  Property creations per day
                </div>
                <apexchart
                  v-if="creationsChart.series[0].data.length"
                  type="bar"
                  height="300"
                  :options="creationsChart.options"
                  :series="creationsChart.series"
                ></apexchart>
                <p v-else class="text-caption text-grey mb-0">No data in range.</p>
              </v-card>
            </v-col>
            <v-col cols="12" lg="6">
              <v-card outlined class="pa-4 mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  Properties published per day
                </div>
                <apexchart
                  v-if="publishesChart.series[0].data.length"
                  type="bar"
                  height="300"
                  :options="publishesChart.options"
                  :series="publishesChart.series"
                ></apexchart>
                <p v-else class="text-caption text-grey mb-0">No data in range.</p>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="pa-4 mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  Platform totals from daily counter snapshot
                </div>
                <apexchart
                  v-if="counterChart.series[0].data.length"
                  type="line"
                  height="320"
                  :options="counterChart.options"
                  :series="counterChart.series"
                ></apexchart>
                <p v-else class="text-caption text-grey mb-0">
                  No counter log entries in this range.
                </p>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item>
          <v-card outlined class="pa-4 mb-4">
            <div class="d-flex align-center mb-2">
              <div class="text-subtitle-1 font-weight-bold">
                Guidebook visits per day
              </div>
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="visitsMetric" mandatory dense>
                <v-btn value="count" small>All sessions</v-btn>
                <v-btn value="unique" small>Unique properties</v-btn>
              </v-btn-toggle>
            </div>
            <apexchart
              v-if="visitsChart.series[0].data.length"
              type="line"
              height="320"
              :options="visitsChart.options"
              :series="visitsChart.series"
            ></apexchart>
            <p v-else class="text-caption text-grey mb-0">No guest visits in range.</p>
          </v-card>

          <v-row>
            <v-col cols="12" lg="6">
              <v-card outlined class="pa-4 mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-3">
                  Top properties by visits
                </div>
                <ehc-table
                  :headers="topPropertyHeaders"
                  :items="data?.topPropertiesByVisits || []"
                  pagination="auto"
                  :itemsPerPage="10"
                  @click:row="goToProperty"
                ></ehc-table>
              </v-card>
            </v-col>
            <v-col cols="12" lg="6">
              <v-card outlined class="pa-4 mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-3">
                  Top visitor locations
                </div>
                <ehc-table
                  :headers="locationHeaders"
                  :items="data?.topLocationsByVisits || []"
                  pagination="auto"
                  :itemsPerPage="10"
                ></ehc-table>
                <p class="text-caption text-grey mt-3 mb-0">
                  Based on IP geolocation for visits logged after this update.
                  Older visits show as Unknown.
                </p>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-card outlined class="pa-4 mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-3">
                  Recent visits
                </div>
                <ehc-table
                  :headers="recentVisitHeaders"
                  :items="recentVisitsFormatted"
                  pagination="auto"
                  :itemsPerPage="10"
                  @click:row="goToPropertyFromVisit"
                ></ehc-table>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item>
          <v-row>
            <v-col cols="12" lg="6">
              <v-card outlined class="pa-4 mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  Guest info submissions per day
                </div>
                <apexchart
                  v-if="guestSubmissionsChart.series[0].data.length"
                  type="bar"
                  height="280"
                  :options="guestSubmissionsChart.options"
                  :series="guestSubmissionsChart.series"
                ></apexchart>
                <p v-else class="text-caption text-grey mb-0">No submissions in range.</p>
              </v-card>
            </v-col>
            <v-col cols="12" lg="6">
              <v-card outlined class="pa-4 mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  Content updates per day
                </div>
                <apexchart
                  v-if="contentUpdatesChart.series[0].data.length"
                  type="bar"
                  height="280"
                  :options="contentUpdatesChart.options"
                  :series="contentUpdatesChart.series"
                ></apexchart>
                <p v-else class="text-caption text-grey mb-0">No updates in range.</p>
              </v-card>
            </v-col>
          </v-row>

          <v-card outlined class="pa-4 mb-4">
            <div class="d-flex align-center mb-3">
              <div class="text-subtitle-1 font-weight-bold">API usage by type</div>
              <v-spacer></v-spacer>
              <router-link to="/analytics_api">Open full API analytics</router-link>
            </div>
            <ehc-table
              :headers="apiHeaders"
              :items="data?.apiByType || []"
              pagination="auto"
              :itemsPerPage="15"
            ></ehc-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </ehc-sheet>
  </ehc-page>
</template>

<script>
import mixins from "@/mixins";
import auth from "@/mixins/auth";
import apiMixin from "@/mixins/api.vue";

function formatChartDate(isoDate) {
  if (!isoDate) {
    return "";
  }
  const parts = isoDate.split("-");
  if (parts.length === 3) {
    return `${parts[1]}/${parts[2]}`;
  }
  return isoDate;
}

function buildBarChart(seriesName, rows) {
  const categories = (rows || []).map((row) => formatChartDate(row.date));
  const data = (rows || []).map((row) => row.count);
  return {
    options: {
      chart: { toolbar: { show: false } },
      xaxis: { categories },
      dataLabels: { enabled: false },
    },
    series: [{ name: seriesName, data }],
  };
}

function buildLineChart(seriesName, rows, valueKey = "count") {
  const categories = (rows || []).map((row) => formatChartDate(row.date));
  const data = (rows || []).map((row) => row[valueKey] || 0);
  return {
    options: {
      chart: { toolbar: { show: false } },
      stroke: { curve: "smooth" },
      xaxis: { categories },
      dataLabels: { enabled: false },
    },
    series: [{ name: seriesName, data }],
  };
}

export default {
  name: "AdvancedAnalytics",
  mixins: [mixins, auth, apiMixin],
  data() {
    return {
      loading: false,
      error: null,
      data: null,
      dateRange: 30,
      activeTab: 0,
      visitsMetric: "count",
      dateRangeOptions: [
        { text: "Last 7 days", value: 7 },
        { text: "Last 30 days", value: 30 },
        { text: "Last 90 days", value: 90 },
      ],
      topPropertyHeaders: [
        { text: "Property", value: "name", sortable: true },
        { text: "Property ID", value: "propertyId", sortable: true },
        { text: "Org ID", value: "orgId", sortable: true },
        { text: "Visits", value: "visits", sortable: true },
      ],
      recentVisitHeaders: [
        { text: "Property", value: "propertyName", sortable: true },
        { text: "Property ID", value: "propertyId", sortable: true },
        { text: "Visited from", value: "visitorLocationLabel", sortable: true },
        { text: "Visited", value: "visitedAt", sortable: true },
      ],
      locationHeaders: [
        { text: "Location", value: "location", sortable: true },
        { text: "Visits", value: "visits", sortable: true },
      ],
      apiHeaders: [
        { text: "API", value: "api", sortable: true },
        { text: "Calls", value: "calls", sortable: true },
        {
          text: "Est. cost",
          value: "costDisplay",
          sortable: true,
        },
      ],
    };
  },
  computed: {
    summaryCards() {
      const s = this.data?.summary || {};
      return [
        { label: "Created", value: this.formatNumber(s.propertiesCreated) },
        { label: "Published", value: this.formatNumber(s.propertiesPublished) },
        { label: "Guidebook visits", value: this.formatNumber(s.guestVisits) },
        {
          label: "Properties visited",
          value: this.formatNumber(s.uniquePropertiesVisited),
        },
        {
          label: "Guest submissions",
          value: this.formatNumber(s.guestSubmissions),
        },
        { label: "Content updates", value: this.formatNumber(s.contentUpdates) },
        { label: "API calls", value: this.formatNumber(s.apiCalls) },
        {
          label: "API cost",
          value: s.apiCost != null ? `$${Number(s.apiCost).toFixed(2)}` : "—",
        },
        {
          label: "Cache hit rate",
          value: s.cacheHitRate != null ? `${s.cacheHitRate}%` : "—",
        },
      ];
    },
    creationsChart() {
      return buildBarChart(
        "Creations",
        this.data?.series?.creationsByDay || [],
      );
    },
    publishesChart() {
      return buildBarChart(
        "Published",
        this.data?.series?.publishesByDay || [],
      );
    },
    counterChart() {
      const rows = this.data?.counterLog || [];
      return {
        options: {
          chart: { toolbar: { show: false } },
          stroke: { curve: "smooth" },
          xaxis: { categories: rows.map((row) => formatChartDate(row.date)) },
          dataLabels: { enabled: false },
        },
        series: [
          { name: "Total properties", data: rows.map((row) => row.total) },
          { name: "Published", data: rows.map((row) => row.published) },
        ],
      };
    },
    visitsChart() {
      const key = this.visitsMetric === "unique" ? "unique" : "count";
      const label =
        this.visitsMetric === "unique" ? "Unique properties" : "Visits";
      return buildLineChart(
        label,
        this.data?.series?.visitsByDay || [],
        key,
      );
    },
    guestSubmissionsChart() {
      return buildBarChart(
        "Submissions",
        this.data?.series?.guestSubmissionsByDay || [],
      );
    },
    contentUpdatesChart() {
      return buildBarChart(
        "Updates",
        this.data?.series?.contentUpdatesByDay || [],
      );
    },
    recentVisitsFormatted() {
      return (this.data?.recentVisits || []).map((visit) => ({
        ...visit,
        propertyName: visit.propertyName || visit.propertyId || "—",
        visitorLocationLabel: visit.visitorLocationLabel || "Unknown",
        visitedAt: visit.dateTime
          ? this.formatDate(visit.dateTime, "dateAndTime")
          : "—",
      }));
    },
  },
  mounted() {
    this.loadAnalytics();
  },
  methods: {
    formatNumber(value) {
      if (value == null) {
        return "—";
      }
      return Number(value).toLocaleString();
    },
    formatRangeLabel(range) {
      if (!range?.start || !range?.end) {
        return "";
      }
      const start = this.formatDate(range.start, "dateOnly");
      const end = this.formatDate(range.end, "dateOnly");
      return `Showing last ${range.days} days (${start} – ${end})`;
    },
    async loadAnalytics() {
      this.loading = true;
      this.error = null;
      try {
        const payload = await this.apiGetAdvancedAnalytics(this.dateRange);
        this.data = {
          ...payload,
          apiByType: (payload.apiByType || []).map((row) => ({
            ...row,
            costDisplay: `$${Number(row.cost || 0).toFixed(4)}`,
          })),
        };
      } catch (err) {
        console.error("loadAnalytics", err);
        this.error =
          err?.message || "Failed to load advanced analytics. Deploy getAdvancedAnalytics if needed.";
        this.$store.commit("setAlert", {
          type: "error",
          text: this.error,
        });
      } finally {
        this.loading = false;
      }
    },
    goToProperty(row) {
      if (row?.propertyId) {
        this.$router.push(`/property/${row.propertyId}`);
      }
    },
    goToPropertyFromVisit(row) {
      if (row?.propertyId) {
        this.$router.push(`/property/${row.propertyId}`);
      }
    },
  },
};
</script>

<style scoped>
.fill-height {
  min-height: 88px;
}
</style>
